import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Manager,
  ManagerPage,
  ManagerPageQuery,
  ManagerRequest,
  ManagerUpdate,
} from "../../types/manager.definition";

// Initial state
interface ManagersState {
  managers: Manager[];
  selectedManager: Manager | null;
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  /** MED-1635: the managers list page, paged by the server. */
  page: {
    /** The last query requested — what a reload asks for again. */
    query: ManagerPageQuery | null;
    content: Manager[];
    totalElements: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
  };
}

const initialState: ManagersState = {
  managers: [],
  selectedManager: null,
  loading: false,
  error: null,
  lastFetch: null,
  page: {
    query: null,
    content: [],
    totalElements: 0,
    totalPages: 0,
    loading: false,
    error: null,
  },
};

// Action creators for saga
export const fetchManagersRequest = createAction(
  "managers/fetchManagersRequest"
);
export const fetchManagersSuccess = createAction<Manager[]>(
  "managers/fetchManagersSuccess"
);
export const fetchManagersFailure = createAction<string>(
  "managers/fetchManagersFailure"
);

// MED-1635: the paged list of the managers page
export const fetchManagersPageRequest = createAction<ManagerPageQuery>(
  "managers/fetchManagersPageRequest"
);
export const fetchManagersPageSuccess = createAction<ManagerPage>(
  "managers/fetchManagersPageSuccess"
);
export const fetchManagersPageFailure = createAction<string>(
  "managers/fetchManagersPageFailure"
);
/** Re-requests the last page query, if the list page has loaded one. */
export const reloadManagersPage = createAction("managers/reloadManagersPage");

export const fetchManagerByIdRequest = createAction<string>(
  "managers/fetchManagerByIdRequest"
);
export const fetchManagerByIdSuccess = createAction<Manager>(
  "managers/fetchManagerByIdSuccess"
);
export const fetchManagerByIdFailure = createAction<string>(
  "managers/fetchManagerByIdFailure"
);

export const createManagerRequest = createAction<ManagerRequest>(
  "managers/createManagerRequest"
);
export const createManagerSuccess = createAction<Manager>(
  "managers/createManagerSuccess"
);
export const createManagerFailure = createAction<string>(
  "managers/createManagerFailure"
);

export const updateManagerRequest = createAction<{
  managerId: string;
  data: ManagerUpdate;
}>("managers/updateManagerRequest");
export const updateManagerSuccess = createAction<Manager>(
  "managers/updateManagerSuccess"
);
export const updateManagerFailure = createAction<string>(
  "managers/updateManagerFailure"
);

// Slice
const managersSlice = createSlice({
  name: "managers",
  initialState,
  reducers: {
    setSelectedManager: (state, action: PayloadAction<Manager | null>) => {
      state.selectedManager = action.payload;
    },
    clearManagers: state => {
      state.managers = [];
      state.selectedManager = null;
      state.lastFetch = null;
    },
    updateManagerInList: (state, action: PayloadAction<Manager>) => {
      const index = state.managers.findIndex(
        manager => manager.id === action.payload.id
      );
      if (index !== -1) {
        state.managers[index] = action.payload;
      }
      // Update selected manager if it's the same
      if (state.selectedManager?.id === action.payload.id) {
        state.selectedManager = action.payload;
      }
    },
    removeManagerFromList: (state, action: PayloadAction<number>) => {
      state.managers = state.managers.filter(
        manager => manager.id !== action.payload
      );
      // Clear selected manager if it was removed
      if (state.selectedManager?.id === action.payload) {
        state.selectedManager = null;
      }
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch managers
    builder
      .addCase(fetchManagersRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagersSuccess, (state, action) => {
        state.loading = false;
        state.managers = action.payload;
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchManagersFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch a page of managers (MED-1635)
    builder
      .addCase(fetchManagersPageRequest, (state, action) => {
        state.page.query = action.payload;
        state.page.loading = true;
        state.page.error = null;
      })
      .addCase(fetchManagersPageSuccess, (state, action) => {
        state.page.loading = false;
        state.page.content = action.payload.content;
        state.page.totalElements = action.payload.totalElements;
        state.page.totalPages = action.payload.totalPages;
      })
      .addCase(fetchManagersPageFailure, (state, action) => {
        state.page.loading = false;
        state.page.error = action.payload;
      });

    // Fetch manager by ID
    builder
      .addCase(fetchManagerByIdRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchManagerByIdSuccess, (state, action) => {
        state.loading = false;
        state.selectedManager = action.payload;
        state.error = null;

        // Update or add to managers list
        const existingIndex = state.managers.findIndex(
          manager => manager.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.managers[existingIndex] = action.payload;
        } else {
          state.managers.push(action.payload);
        }
      })
      .addCase(fetchManagerByIdFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create manager
    builder
      .addCase(createManagerRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createManagerSuccess, (state, action) => {
        state.loading = false;
        state.managers.push(action.payload);
        state.error = null;
      })
      .addCase(createManagerFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update manager
    builder
      .addCase(updateManagerRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateManagerSuccess, (state, action) => {
        state.loading = false;
        const index = state.managers.findIndex(
          manager => manager.id === action.payload.id
        );
        if (index !== -1) {
          state.managers[index] = action.payload;
        }
        // Update selected manager if it's the same
        if (state.selectedManager?.id === action.payload.id) {
          state.selectedManager = action.payload;
        }
        state.error = null;
      })
      .addCase(updateManagerFailure, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export slice actions
export const {
  setSelectedManager,
  clearManagers,
  updateManagerInList,
  removeManagerFromList,
  clearError,
} = managersSlice.actions;

// Export selectors
export const selectManagers = (state: { managers: ManagersState }) =>
  state.managers.managers;
export const selectSelectedManager = (state: { managers: ManagersState }) =>
  state.managers.selectedManager;
export const selectManagersLoading = (state: { managers: ManagersState }) =>
  state.managers.loading;
export const selectManagersError = (state: { managers: ManagersState }) =>
  state.managers.error;
export const selectLastFetch = (state: { managers: ManagersState }) =>
  state.managers.lastFetch;

// MED-1635: the managers list page
export const selectManagersPageContent = (state: { managers: ManagersState }) =>
  state.managers.page.content;
export const selectManagersPageTotalElements = (state: {
  managers: ManagersState;
}) => state.managers.page.totalElements;
export const selectManagersPageTotalPages = (state: {
  managers: ManagersState;
}) => state.managers.page.totalPages;
export const selectManagersPageLoading = (state: { managers: ManagersState }) =>
  state.managers.page.loading;
export const selectManagersPageError = (state: { managers: ManagersState }) =>
  state.managers.page.error;
export const selectManagersPageQuery = (state: { managers: ManagersState }) =>
  state.managers.page.query;

// Helper selector to check if data is stale (older than 5 minutes)
export const selectIsDataStale = (state: { managers: ManagersState }) => {
  if (!state.managers.lastFetch) return true;
  return Date.now() - state.managers.lastFetch > 5 * 60 * 1000; // 5 minutes
};

export default managersSlice.reducer;
